import React, {
  FC, useMemo, useState, useEffect, useCallback, useRef,
} from 'react';

import classnames from 'classnames';
import _, { NumericDictionary } from 'lodash';
import { Box, useSelectionContainer, boxesIntersect } from 'react-drag-to-select';
import Checkbox from 'app/components/general/Checkbox';
import Table from 'app/components/general/Table';
import BadgeWithTooltip from 'app/components/general/BadgeWithTooltip';
import Notification from 'app/components/general/Notification';

import { IPaymentEvent } from 'app/ducks/paymentEvents/types';
import * as paymentEventsSelectors from 'app/ducks/paymentEvents/selectors';

export interface IMatchRow {
  [id: string]: {
    selected?: boolean;
    id?: string;
    amount?: number;
  };
}

const MouseSelection = React.memo(({ onSelectionChange, onSelectionEnd }) => {
  const { DragSelection } = useSelectionContainer({
    // eventsElement: document.getElementById('team-members-selectable'),
    onSelectionChange,
    selectionProps: {
      style: {
        border: '1px solid #387abf',
        backgroundColor: '#d2e8ff',
        opacity: 0.5,
        zIndex: 999,
        position: 'absolute',
      },
    },
  });

  return <DragSelection />;
});

const renderCheckboxInput = ({
  value,
  row: { index, original: { id, teamName } },
  rows,
  column: {
    selectRow, setSelectedIndexes, selectedIndexes,
  },
}): React.ReactNode => (
  <div className="d-flex align-items-center">
    <Checkbox
      key={`${id}-checkbox`}
      onMouseDown={(e) => { e.stopPropagation(); }}
      onMouseMove={(e) => { e.stopPropagation(); }}
      onClick={(e) => {
        e.stopPropagation();
        const ids = [id];
        const finalIds = _.reduce(selectedIndexes, (result, indexValue, key) => {
          if (key !== value && indexValue) {
            result.push(key);
          }
          return result;
        }, ids);
        selectRow(finalIds);
        setSelectedIndexes({});
      }}
      checked={value || false}
    />
    {teamName}
  </div>
);

const renderCheckboxHeader = ({
  column: { selectAllRows, selectedAllRows },
}) => (
  <div className="d-flex align-items-center">
    <Checkbox
      onClick={(e) => {
        e.stopPropagation();
        selectAllRows();
      }}
      checked={selectedAllRows}
    />
    {' '}
    Team
  </div>
);

interface IProps {
  actions: any;
  paymentEventsById: IPaymentEvent[];
  paymentEventIds: string[];
  teamPaymentRequests: any;
  teamId?: string;
  headerTitle?: string;
  appReady?: boolean;
  isLoading?: boolean;
  manual?: boolean;
  hideActions?: boolean;
  hideAmount?: boolean;
  showPayMultipleMatchesButton?: boolean;
  aggressiveLoader?: boolean;
  saveFilters?: boolean;
  initialFilterConfig?: any;
  persistentFilterConfig?: any;
  pagination?: any;
  groupIds: {
    teamId: string;
    groupId: string;
  }[];
  groupsById: any;
  selectTeam: Function;
  selectPayment: Function;
  handleClick?: Function;
  updateSelectedRows?: Function;
  selectedAllRows?: boolean;
  selectRow?: Function;
  selectAllRows?: Function;
  removeRow?: Function;
  removeAllRows?: Function;
  selectedRows?: any;
  selectedRowIds?: string[];
  selectedRowsById?: any;
  rows?: IMatchRow[];
}

const MatchesTableSelectable: FC<IProps> = (props: IProps = {
  manual: true,
  actions: undefined,
  teamPaymentRequests: undefined,
  paymentEventsById: undefined,
  paymentEventIds: undefined,
  initialFilterConfig: undefined,
  persistentFilterConfig: undefined,
  isLoading: false,
  pagination: undefined,
  selectTeam: undefined,
  aggressiveLoader: true,
  showPayMultipleMatchesButton: false,
  selectPaymentEvent: undefined,
  selectedRows: undefined,
  selectedRowIds: [],
  selectedAllRows: false,
  selectRow: undefined,
  selectAllRows: undefined,
  removeRow: undefined,
  removeAllRows: undefined,
  updateSelectedRows: undefined,
}) => {
  const filterOptions = [
    {
      label: 'Team',
      accessor: 'groupName',
      type: 'text',
    },
    {
      label: 'Round',
      accessor: 'roundName',
      type: 'text',
    },
    {
      label: 'Status',
      accessor: 'ignoreStatus',
      type: 'select',
      options: [
        { id: 'pending', name: 'Unpaid' },
        { id: 'paid', name: 'Paid' },
        { id: 'refunded', name: 'Refunded' },
        { id: 'forfeited', name: 'Forfeited' },
        { id: 'all', name: 'All' },
      ],
    },
  ];

  const [selectionBox, setSelectionBox] = useState<Box>();
  const [filteredRows, setFilteredRows] = useState<boolean>(false);
  const [selectedIndexes, setSelectedIndexes] = useState<{ [key: string]: boolean }>({});
  const selectableItems = useRef<{ [key: string]: Box }>({});
  const selectionId = 'matches-selectable-rows';

  useEffect(() => {
    selectableItems.current = {};
    const elementsContainer = document.getElementById(selectionId);
    if (elementsContainer) {
      Array.from(elementsContainer.childNodes).forEach((item) => {
        // @ts-ignore
        const {
          left, top, width, height,
        } = item.getBoundingClientRect();

        selectableItems.current[_.replace(item.id, '-row', '')] = {
          left,
          top,
          width,
          height,
        };
      });
    }
  });

  const handleSelectionChange = useCallback(
    (box: Box) => {
      setSelectionBox(box);
      const indexesToSelect: { [key: string]: boolean } = {};
      _.forEach(selectableItems.current, (value, key) => {
        if (boxesIntersect({ ...box, top: box.top + window.scrollY }, value)) {
          indexesToSelect[key] = true;
        } else {
          indexesToSelect[key] = false;
        }
      });

      setSelectedIndexes(indexesToSelect);
    },
    [selectableItems],
  );

  useEffect(() => {
    setSelectedIndexes({});
  }, [JSON.stringify(props.pagination)]);

  useEffect(() => {
    setSelectedIndexes({});
  }, [JSON.stringify(props.rows)]);

  const fetchDataFromApi = ({
    sort, filter, pageNumber, pageSize,
  }): void => {
    const payload = {
      page: {
        size: pageSize,
        number: pageNumber,
      },
      sort,
      filter,
    };
    props.actions.getAllPaymentEventsForAllTeams(payload);
  };

  useEffect(() => {
    if (!_.isEmpty(props.paymentEventIds)) {
      props.actions.getAllTeamPaymentRequests(
        {
          filter: {
            'match-payment-forfeit-id': _.uniq(_.flatMap(props.paymentEventsById, (p) => {
              const returnIds = [];
              const groupIds = _.map(props.groupIds, (g) => g.groupId);
              if (_.includes(groupIds, p.groupOne.id)) {
                returnIds.push(p.groupOneMatchPayment.id);
                returnIds.push(p.groupOneForfeit.id);
              }
              if (_.includes(groupIds, p.groupTwo.id)) {
                returnIds.push(p.groupTwoMatchPayment.id);
                returnIds.push(p.groupTwoForfeit.id);
              }
              return returnIds;
            })).join(','),
          },
          page: {
            size: 200,
          },
        },
        false,
      );
    }
  }, [JSON.stringify(props.paymentEventsById)]);

  const data = useMemo(() => {
    const paymentStatus: string = _.find(props.pagination?.filter, (item) => item.id === 'ignoreStatus')?.value;

    if (_.isEmpty(props.paymentEventsById) || props.paymentEventIds === undefined) {
      return [];
    }

    const rows = _.reduce(
      props.paymentEventIds,
      (acc, id) => {
        const pe = props.paymentEventsById[id];
        if (!_.isEmpty(pe)) {
          const teamGroup1 = _.find(props.groupIds, (t) => t.groupId === pe.groupOne.id);
          const teamGroup2 = _.find(props.groupIds, (t) => t.groupId === pe.groupTwo.id);

          if (teamGroup1 !== undefined) {
            const validGroup1 = paymentStatus === undefined || paymentEventsSelectors.filterPaymentEventByStatus(
              pe,
              teamGroup1?.groupId,
              paymentStatus,
            );
            if (validGroup1 && pe.groupOneMatchPaymentType !== 'event-variable') {
              const teamPaymentRequest = _.find(props.teamPaymentRequests.teamPaymentRequestsById, (p) => (!_.isEmpty(p.matchPayment) && p.matchPayment?.id === pe.groupOneMatchPayment?.id) || (!_.isEmpty(p.forfeit) && p.forfeit?.id === pe.groupOneForfeit?.id));

              const peg = paymentEventsSelectors.getPaymentEventForMatchCard(
                pe,
                pe.groupOne.id,
                teamGroup1?.teamId,
                teamPaymentRequest, selectedIndexes[pe.groupOneMatchPayment?.id], props.rows,
              );
              if (_.includes(['pending'], peg.statusBlock.paymentStatus)
                && peg.paymentEnabled
                && !_.includes(['walkover'], peg.statusBlock.forfeitStatus)
                && !_.includes(['cancelled', 'paused'], peg.matchStatus)
                && !_.includes(['no-show'], peg.statusBlock.adminStatus)
                && !_.includes(['walkover'], peg.otherTeamStatusBlock.forfeitStatus)
                && !_.includes(['no-show'], peg.otherTeamStatusBlock.adminStatus)) {
                acc.push(peg);
              }
            }
          }
          if (teamGroup2 !== undefined && pe.groupTwoMatchPaymentType !== 'event-variable') {
            const validGroup2 = paymentStatus === undefined || paymentEventsSelectors.filterPaymentEventByStatus(
              pe,
              teamGroup2?.groupId,
              paymentStatus,
            );

            if (validGroup2) {
              const teamPaymentRequest = _.find(props.teamPaymentRequests.teamPaymentRequestsById, (p) => (!_.isEmpty(p.matchPayment) && p.matchPayment?.id === pe.groupTwoMatchPayment?.id) || (!_.isEmpty(p.forfeit) && p.forfeit?.id === pe.groupTwoForfeit?.id));
              const peg = paymentEventsSelectors.getPaymentEventForMatchCard(
                pe,
                pe.groupTwo.id,
                teamGroup2?.teamId,
                teamPaymentRequest, selectedIndexes[pe.groupTwoMatchPayment?.id], props.rows,
              );
              if (_.includes(['pending'], peg.statusBlock.paymentStatus)
                && peg.paymentEnabled
                && !_.includes(['walkover'], peg.statusBlock.forfeitStatus)
                && !_.includes(['cancelled', 'paused'], peg.matchStatus)
                && !_.includes(['no-show'], peg.statusBlock.adminStatus)
                && !_.includes(['walkover'], peg.otherTeamStatusBlock.forfeitStatus)
                && !_.includes(['no-show'], peg.otherTeamStatusBlock.adminStatus)) {
                acc.push(peg);
              }
            }
          }

          if (pe.groupOneMatchPaymentType === 'event-variable' || pe.groupTwoMatchPaymentType === 'event-variable') {
            setFilteredRows(true);
          }
        }
        return acc;
      },
      [],
    );

    return rows;
  }, [JSON.stringify(props.paymentEventsById),
    props.paymentEventIds,
    props.groupIds,
    JSON.stringify(props.teamPaymentRequests.paymentEventsById),
    selectedIndexes]);

  const renderStatus = (cellProps: {
    value: {
      id: string;
      status: string;
      statuses: string[];
    };
  }): React.ReactNode => {
    const targetId = `t-${cellProps.value.id}`;

    return (
      <div className="payment-badge-group">
        {_.map(cellProps.value.statuses, (status) => (
          <BadgeWithTooltip className="payment-badge" targetId={targetId} key={status.text} color={status.colour} tooltipText={status.description}>
            {status.text}
          </BadgeWithTooltip>
          // <StatusBadge
          //   paymentText={status}
          //   tick={false}
          //   className="no-margin-right"
          // />
        ))}
      </div>
    );
  };

  const renderRound = (cellProps: {
    value: {
      roundName: string;
      roundDate: string;
    };
  }): React.ReactNode => (
    <span className="d-inline">
      {cellProps.value.roundName}
      <span className="margin-left-5 text-weight-medium-bold">
        {` - ${cellProps.value.roundDate}`}
      </span>
    </span>
  );

  const tinyColumn = 'col d-none d-lg-flex col-3 col-md-2 col-lg-1';
  const smallColumn = 'col col-3 col-lg-2';
  const growColumn = 'col d-flex';
  const mediumColumn = 'col col-4 col-md-3 col-lg-3 col-xl-3';
  const largeColumn = 'col col-4 col-md-4 col-lg-4 col-xl-3';

  const columns = useMemo(() => {
    const columnsParsed = [
      {
        Header: renderCheckboxHeader,
        accessor: 'selected',
        id: 'select',
        className: 'col col-5 col-sm-4 word-wrap-anywhere',
        headerClassName: 'col col-5 col-sm-4',
        Cell: renderCheckboxInput,
        selectRow: (finalIds) => props.selectRow(finalIds),
        setSelectedIndexes,
        selectedIndexes,
        selectedAllRows: props.selectedAllRows,
        selectAllRows: props.selectAllRows,
        // disableSortBy: true,
      },
      {
        Header: 'Round',
        id: 'roundName',
        accessor: (row) => ({ roundName: row.round, roundDate: row.dateTime }),
        className: mediumColumn,
        headerClassName: mediumColumn,
        Cell: renderRound,
      },
      {
        id: 'statusCol',
        sortable: false,
        Header: 'Status',
        className: largeColumn,
        isVisble: !props.hideActions,
        headerClassName: largeColumn,
        accessor: (row) => ({
          id: row.paymentEventId,
          status: row.status,
          statuses: row.statuses,
          matchPaymentId: row.matchPaymentId,
        }),
        Cell: renderStatus,
      },
    ];
    if (props.hideAmount !== true) {
      columnsParsed.push({
        Header: 'Amount',
        sortable: false,
        accessor: 'amount',
        className: smallColumn,
        headerClassName: smallColumn,
      });
    }
    return columnsParsed;
  }, [props.paymentEventsById, JSON.stringify(props.paymentEventIds), JSON.stringify(props.rows), selectedIndexes]);

  // const counterText = props.pagination.meta.totalCount === 1 ? '1 match' : `${props.pagination.meta.totalCount || 0} matches`;

  return (
    <div>
      <Notification className={classnames({ 'd-none': !filteredRows, 'margin-bottom-15': filteredRows })}>
        <div className="d-inline-block">
          Only available for matches with a fixed match fee.
        </div>
      </Notification>
      <MouseSelection onSelectionChange={handleSelectionChange} />
      <Table
        data={data}
        columns={columns}
        isLoading={props.isLoading}
        aggressiveLoader={props.aggressiveLoader}
        // headerTitle={' '}
        objectTitle="matches"
        filterTitle="Matches Data"
        infoText="Matches must all be within a single organisation"
        handleClick={undefined}
        filterable
        saveFilters={props.saveFilters}
        manual={props.manual}
        fetchDataFromApi={fetchDataFromApi}
        filterOptions={filterOptions}
        filterConfig={props.initialFilterConfig}
        persistentFilterConfig={props.persistentFilterConfig}
        pagination={props.pagination}
        showPagination
        rowSelectableContainerId={selectionId}
      >
        {/* {props.showPayMultipleMatchesButton && (
          <Button onClick={() => { history.push('/matches/pay-multiple'); }} noLoading className="btn-thinner btn-2">
            <Isvg src={DollarIcon} className="btn-icon" />
            Pay Multiple
          </Button>
        )} */}
      </Table>
    </div>
  );
};

export default MatchesTableSelectable;
